import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useAuth } from '../lib/auth.tsx';
import { ActionDetailModal } from '../components/ActionDetailModal.tsx';
import { useCountUp } from '../lib/useCountUp.ts';
import { useRounds } from '../lib/rounds.ts';
import { EventDetailModal } from '../components/EventDetailModal.tsx';
import { ErrorState, Loading, PageHeader, PriorityBadge } from '../components/ui.tsx';
import { PlatformBadge } from '../components/PlatformBadge.tsx';
import {
  EVENT_TYPE_LABEL,
  describeAge,
  describeDeadline,
  formatDate,
  formatDateLong,
  formatNumber,
  metricLabel,
  todayIso,
} from '../lib/format.ts';
import type { CalendarEvent, DashboardOverview } from '../../../shared/types.ts';

// Mesmo número nas duas listas do par "split-2" (Próximas ações / O que vem por aí) —
// senão uma mostra 5 itens e a outra mostra os 8 que o backend devolve, e o par fica
// com quantidades de conteúdo visivelmente diferentes mesmo depois do card esticar.
const VISIBLE_NEXT_ACTIONS = 5;
const VISIBLE_UPCOMING_EVENTS = 5;

export function OverviewPage() {
  const { user, can } = useAuth();
  const { data, error, loading, reload } = useAsync(
    () => api.get<DashboardOverview>('/dashboard/overview'),
    [],
  );
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<CalendarEvent | null>(null);
  const rounds = useRounds();

  if (loading) return <Loading label="Montando a visão geral…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const { actionCounts, nextActions, upcomingEvents, latestNews, keyMetrics, auditRounds } = data;
  // A rodada mais recente é a maior data entre as rodadas da auditoria (seed) e as rodadas importadas (série temporal).
  const lastRoundDate = [auditRounds[0]?.roundId, rounds.latest?.roundDate].filter((d): d is string => Boolean(d)).sort().at(-1) ?? null;
  const liveUntil = keyMetrics.reduce<string | null>((max, m) => (!max || m.date > max ? m.date : max), null);
  const open = actionCounts.byStatus.pendente + actionCounts.byStatus.em_andamento;

  return (
    <>
      <PageHeader
        title={`Olá, ${user?.name?.split(' ')[0] ?? ''}`}
        subtitle={
          [
            lastRoundDate && `Última rodada de auditoria: ${formatDate(lastRoundDate)} (${describeAge(lastRoundDate)}).`,
            liveUntil && `Números principais coletados até ${formatDate(liveUntil)}.`,
            `${open} ${open === 1 ? 'ação' : 'ações'} em aberto.`,
          ]
            .filter(Boolean)
            .join(' ')
        }
      />

      <section className="grid-cards" aria-label="Situação das ações">
        <SummaryTile
          label="Em aberto"
          value={open}
          valueSuffix={
            actionCounts.byStatus.em_andamento > 0
              ? `(${actionCounts.byStatus.em_andamento} em andamento)`
              : undefined
          }
        />
        <SummaryTile
          label="Atrasadas"
          value={actionCounts.overdue}
          tone={actionCounts.overdue > 0 ? 'danger' : 'neutral'}
          hint="Prazo já vencido"
        />
        <SummaryTile
          label="Vencem em 7 dias"
          value={actionCounts.dueSoon}
          tone={actionCounts.dueSoon > 0 ? 'warning' : 'neutral'}
          hint="Próxima semana"
        />
        <SummaryTile
          label="Concluídas"
          value={actionCounts.byStatus.concluida}
          tone="success"
          hint={
            actionCounts.completedLast7Days > 0
              ? `+${actionCounts.completedLast7Days} nos últimos 7 dias`
              : 'Desde o início'
          }
          hintIsGoodNews={actionCounts.completedLast7Days > 0}
        />
      </section>

      <div className="split-2">
        <section className="card" aria-labelledby="proximas-acoes">
          <div className="card__header">
            <h2 className="text-title" id="proximas-acoes">
              Próximas ações
            </h2>
            <Link className="btn btn-ghost btn-sm" to="/acoes">
              Ver todas
            </Link>
          </div>

          {nextActions.length === 0 ? (
            <p className="text-muted text-label">Nada em aberto. Bom trabalho.</p>
          ) : (
            <>
              <ul className="lined-list">
                {nextActions.slice(0, VISIBLE_NEXT_ACTIONS).map((action) => {
                  const deadline = describeDeadline(action.dueDate);
                  const late = action.dueDate ? action.dueDate < todayIso() : false;
                  return (
                    <li key={action.id}>
                      <button
                        type="button"
                        className="lined-list__link"
                        onClick={() => setOpenActionId(action.id)}
                      >
                        <div className="lined-list__main">
                          <span className="text-body">{action.title}</span>
                          {action.category && (
                            <span className="text-micro text-muted">{action.category}</span>
                          )}
                        </div>
                        <div className="lined-list__meta">
                          <PriorityBadge priority={action.priority} />
                          {deadline && (
                            <span className={`text-micro ${late ? 'text-danger' : 'text-muted'}`}>
                              {deadline}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {open > VISIBLE_NEXT_ACTIONS && (
                <Link className="lined-list__more text-label link" to="/acoes">
                  +{open - VISIBLE_NEXT_ACTIONS} {open - VISIBLE_NEXT_ACTIONS === 1 ? 'ação em aberto' : 'ações em aberto'}
                </Link>
              )}
            </>
          )}
        </section>

        <section className="card" aria-labelledby="proximo-calendario">
          <div className="card__header">
            <h2 className="text-title" id="proximo-calendario">
              O que vem por aí
            </h2>
            <Link className="btn btn-ghost btn-sm" to="/calendario">
              Calendário
            </Link>
          </div>

          {upcomingEvents.length === 0 ? (
            <p className="text-muted text-label">Nenhum evento à frente.</p>
          ) : (
            <>
              <ul className="lined-list">
                {upcomingEvents.slice(0, VISIBLE_UPCOMING_EVENTS).map((event) => (
                  <li key={event.id}>
                    <button type="button" className="lined-list__link" onClick={() => setOpenEvent(event)}>
                      <div className="lined-list__main">
                        <span className="text-body">{event.title}</span>
                        <span className="text-micro text-muted">
                          {EVENT_TYPE_LABEL[event.eventType]}
                        </span>
                      </div>
                      <span className="text-micro text-secondary">{formatDateLong(event.startDate)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {upcomingEvents.length > VISIBLE_UPCOMING_EVENTS && (
                // Sem contagem aqui (diferente do "+X ações em aberto" acima): a API só traz os
                // 8 mais próximos, então "o resto" pode ser bem mais que isso — um número seria
                // só o que sobrou desses 8, não o total real de eventos futuros.
                <Link className="lined-list__more text-label link" to="/calendario">
                  Ver mais no calendário
                </Link>
              )}
            </>
          )}
        </section>
      </div>

      <div className="split-2">
        <section className="card" aria-labelledby="metricas-chave">
          <div className="card__header">
            <h2 className="text-title" id="metricas-chave">
              Métricas principais
            </h2>
            <Link className="btn btn-ghost btn-sm" to="/metricas">
              Detalhar
            </Link>
          </div>

          {keyMetrics.length === 0 ? (
            <p className="text-muted text-label">Nenhuma métrica carregada ainda.</p>
          ) : (
            <ul className="metric-list">
              {keyMetrics.map((m) => (
                <li key={`${m.platform}-${m.metric}`}>
                  <div>
                    <span className="text-body-strong">{metricLabel(m.metric)}</span>
                    <span className="metric-list__source">
                      <PlatformBadge platform={m.platform} />
                      <span className="text-micro text-muted">
                        medido em {formatDate(m.date)}
                      </span>
                    </span>
                  </div>
                  <span className="text-title tabular-nums">{formatNumber(m.value)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card" aria-labelledby="radar-home">
          <div className="card__header">
            <h2 className="text-title" id="radar-home">
              Radar Evanescence
            </h2>
            <Link className="btn btn-ghost btn-sm" to="/noticias">
              Ver radar
            </Link>
          </div>

          {latestNews.length === 0 ? (
            <p className="text-muted text-label">
              Nenhuma notícia coletada ainda — o radar roda a cada poucas horas.
            </p>
          ) : (
            <ul className="lined-list">
              {latestNews.map((item) => (
                <li key={item.id}>
                  <div className="lined-list__main">
                    <a
                      className="text-body link"
                      href={item.url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {item.title}
                    </a>
                    <span className="text-micro text-muted">{item.sourceName}</span>
                  </div>
                  <span className="text-micro text-secondary">
                    {formatDate(item.publishedAt ?? item.fetchedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {openActionId && (
        <ActionDetailModal
          id={openActionId}
          canEdit={can('gerente')}
          onClose={() => setOpenActionId(null)}
          onChanged={reload}
        />
      )}

      {openEvent && (
        <EventDetailModal
          event={openEvent}
          canEditAction={can('gerente')}
          onClose={() => setOpenEvent(null)}
        />
      )}
    </>
  );
}

function SummaryTile({
  label,
  value,
  valueSuffix,
  hint,
  tone = 'neutral',
  hintIsGoodNews = false,
}: {
  label: string;
  value: number;
  valueSuffix?: string;
  hint?: string;
  tone?: 'neutral' | 'danger' | 'warning' | 'success';
  hintIsGoodNews?: boolean;
}) {
  const displayValue = useCountUp(value);
  return (
    <div className={`card stat-tile stat-tile--${tone}`}>
      <span className="text-label text-muted">{label}</span>
      <span className="stat-tile__value">
        <strong className="text-hero tabular-nums">{displayValue}</strong>
        {valueSuffix && <span className="text-label text-muted">{valueSuffix}</span>}
      </span>
      {hint && (
        <span
          className={`text-micro ${hintIsGoodNews ? 'text-success stat-tile__hint--rise' : 'text-muted'}`}
        >
          {hint}
        </span>
      )}
    </div>
  );
}
